import { useState } from 'react';
import { notificationsAPI } from '../utils/api';

// Safely resolve a student name that may be a bilingual object {en, zh} or a plain string.
const resolveStudentName = (student) => {
  const raw = student?.name || student?.studentName || '';
  if (raw && typeof raw === 'object') return raw.en || raw.zh || 'Student';
  return raw || 'Student';
};

export const useStudentAlert = () => {
  const [alertStudent, setAlertStudent] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertMessageZh, setAlertMessageZh] = useState('');
  const [alertTargets, setAlertTargets] = useState(['student', 'parent']);
  const [sendingAlert, setSendingAlert] = useState(false);
  const [toast, setToast] = useState(null); // Local toast for the hook, or you could pass a global toast

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const openAlert = (student, monthItem = null) => {
    setAlertStudent(student);
    setAlertTargets(['student', 'parent']);

    const studentName = resolveStudentName(student);

    if (monthItem) {
      const monthFee = monthItem.expectedAmount || 1800;
      setAlertMessage(
        `Dear parent/student, a friendly reminder that the training fee of $${monthFee} HKD for the month of ${monthItem.month} ${monthItem.year} is currently ${monthItem.status} for student ${studentName}. Please log into the app portal to clear outstanding balances.`
      );
      setAlertMessageZh(
        `家長/學生您好，溫馨提醒：學生 ${studentName} ${monthItem.year}年${monthItem.month}的學費 $${monthFee} HKD 目前狀態為「${monthItem.status}」，請登入學生平台安排付款。`
      );
    } else {
      setAlertMessage(
        `Dear parent/student, a friendly reminder that the global training fee balance of $${student.balance} HKD is currently pending/overdue for student ${studentName}. Please log into the app portal to clear outstanding balances.`
      );
      setAlertMessageZh(
        `家長/學生您好，溫馨提醒：學生 ${studentName} 的學費結餘 $${student.balance} HKD 目前尚未繳清，請登入學生平台安排付款。`
      );
    }
  };

  const closeAlert = () => {
    setAlertStudent(null);
    setAlertMessage('');
  };

  const toggleTarget = (target) => {
    setAlertTargets(prev => 
      prev.includes(target) ? prev.filter(t => t !== target) : [...prev, target]
    );
  };

  const sendAlert = async (e, onSuccessCallback) => {
    if (e) e.preventDefault();
    if (!alertStudent) return;
    if (alertTargets.length === 0) {
      showToast('Please select at least one recipient target.', 'error');
      return;
    }

    setSendingAlert(true);
    try {
      const currentYear = new Date().getFullYear();
      const months = [
        "January", "February", "March", "April", "May", "June", 
        "July", "August", "September", "October", "November", "December"
      ];
      const currentMonthStr = `${months[new Date().getMonth()]} ${currentYear}`;

      await notificationsAPI.send(
        alertStudent._id || alertStudent.id || alertStudent.studentId,
        alertTargets,
        currentMonthStr,
        alertMessage,
        alertMessageZh
      );

      showToast(`Communication reminder dispatched successfully to ${alertTargets.join(' & ')}!`);
      closeAlert();
      
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    } catch (err) {
      console.error('Failed to send targeted alert:', err);
      showToast(err.response?.data?.message || 'Target does not have a linked portal login.', 'error');
    } finally {
      setSendingAlert(false);
    }
  };

  return {
    alertStudent,
    alertMessage,
    setAlertMessage,
    alertMessageZh,
    setAlertMessageZh,
    alertTargets,
    toggleTarget,
    sendingAlert,
    openAlert,
    closeAlert,
    sendAlert,
    toast,
    isAlertOpen: !!alertStudent
  };
};
